import { useEffect, useCallback, PropsWithChildren, FC } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { recordRumError, recordRumEvent } from 'src/analytics/rum';
import {
  APP_INITIALIZED_EVENT,
  RENDER_ERROR_EVENT,
} from 'src/constants/MonitoringConstants';

const ErrorFallback: FC = () => {
  return (
    <p style={errorFallbackStyle}>
      Something went wrong. Please refresh the page.
    </p>
  );
};

/**
 * Wraps the application with an initialization metric and error boundary.
 *
 * The initialization metric fires on first mount. For most real applications,
 * you'll want to delay this until your initial data fetches complete.
 *
 * The error boundary catches render errors and publishes a counter metric.
 *
 * @see https://github.com/bvaughn/react-error-boundary#readme
 */
const AppInitWrapper: FC<PropsWithChildren> = ({ children }) => {
  useEffect(() => {
    recordRumEvent(APP_INITIALIZED_EVENT, {
      methodName: 'AppInitialization',
    });
  }, []);

  const handleError = useCallback(
    (error: Error, info: { componentStack?: string | null }) => {
      recordRumEvent(RENDER_ERROR_EVENT, {
        methodName: 'AppErrorBoundary',
        message: error.message,
        componentStack: info.componentStack ?? undefined,
      });
      recordRumError(error);
    },
    [],
  );

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleError}>
      {children}
    </ErrorBoundary>
  );
};

const errorFallbackStyle = { textAlign: 'center' as const, padding: '2rem' };

export default AppInitWrapper;
